import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';
import styled from 'styled-components';
import DatePicker from 'react-datepicker';
import { ko } from 'date-fns/locale';
import { Calendar, TimeLine } from '../../../components/icon/icon';
import { useEffect, useState } from 'react';
import { Input } from '../../../components/items/joinInput';
import { formatLocalDate, formatNumber } from '../../../utils/util';
import { getCompanyName, ordersRegistrations } from '../../../api/order';
import { zodResolver } from '@hookform/resolvers/zod';
import { orderRegistrationSchema } from '../../../schema/orderSchema';
import { useHistory, useLocation } from 'react-router';
import { AlertModal } from '../../../components/modal/alert.modal';

const carType = [{ value: '믹스트럭', label: '믹스트럭' }];

const workTypeSelectItems = [
  { value: 'full', label: '전일' },
  { value: 'half', label: '반일' },
];

interface OrderState {
  wanted: string;
  targetDate: Date | null;
  targetTime: Date | null;
  work: string;
  totalVehicle: number;
  amount: number;
  orders: {
    targetDate: Date | null;
    targetTime: Date | null;
    work: string;
    totalVehicle: number;
    amount: number;
  }[];
}

// orderCompanyStep2Dto 인터페이스 정의
interface OrderCompanyStep2Dto {
  wanted: string;
  targetDate: string;
  targetTime: string;
  work: string;
  totalVehicle: number; // 문자열이 아닌 숫자형만 허용
  amount: number; // 문자열이 아닌 숫자형만 허용
}

const OrderRegist = () => {
  const today = new Date();
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<OrderState>({
    resolver: zodResolver(orderRegistrationSchema),
    defaultValues: {
      wanted: '',
      targetDate: null,
      targetTime: null,
      work: '',
      totalVehicle: 0,
      amount: 0,
      orders: [],
    },
  });
  const [startTime, setStartTime] = useState<Date>(new Date());
  const [targetDate, setTargetDate] = useState<Date>(today);
  const [orders, setOrders] = useState<OrderState['orders']>([]);

  // 모달
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [navigateOnClose, setNavigateOnClose] = useState(false); // 페이지 이동 여부를 추적

  // 회사 이름 상태값
  const [companyName, setCompanyName] = useState('');

  const addOrder = () => {
    setOrders([
      ...orders,
      {
        targetDate: null,
        targetTime: null,
        work: '',
        totalVehicle: 0,
        amount: 0,
      },
    ]);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    if (navigateOnClose) {
      history.push({
        pathname: '/managerOrder',
      });
    }
  };

  const removeOrder = (index: number) => {
    setOrders(orders.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: OrderState) => {
    // 메인 폼 데이터와 추가된 주문 데이터를 API 스키마에 맞게 변환
    const apiData: OrderCompanyStep2Dto[] = [
      {
        wanted: data.wanted,
        targetDate: formatLocalDate(data.targetDate), // 날짜를 'YYYY-MM-DD' 형식으로 변환
        targetTime: data.targetTime
          ? `${String(data.targetTime.getHours()).padStart(2, '0')}:${String(
              data.targetTime.getMinutes(),
            ).padStart(2, '0')}`
          : '', // 시간을 'HH:mm' 형식으로 변환
        work: data.work,
        totalVehicle: Number(data.totalVehicle), // 숫자형으로 변환
        amount: Number(data.amount), // 숫자형으로 변환
      },
      ...data.orders.map((order: any) => ({
        wanted: data.wanted,
        targetDate: order.targetDate?.toISOString().split('T')[0], // 날짜를 'YYYY-MM-DD' 형식으로 변환
        targetTime: order.targetTime
          ? `${String(order.targetTime.getHours()).padStart(2, '0')}:${String(
              order.targetTime.getMinutes(),
            ).padStart(2, '0')}`
          : '', // 시간을 'HH:mm' 형식으로 변환
        work: order.work,
        totalVehicle: Number(order.totalVehicle), // 숫자형으로 변환
        amount: Number(order.amount), // 숫자형으로 변환
      })),
    ];

    try {
      const response = await ordersRegistrations(apiData);
      console.log(response);
      if (response.statusCode === 200) {
        setModalMessage(response.message);
        setIsModalOpen(true);
        setNavigateOnClose(true);
      } else {
        setModalMessage('기준 금액 설정에 실패했습니다.');
        setIsModalOpen(true);
        setNavigateOnClose(false);
      }
    } catch (error) {
      console.error('Failed to update order:', error);
    }
  };

  useEffect(() => {
    const getCompanyNameData = async () => {
      try {
        const response = await getCompanyName();
        setCompanyName(response.name);
      } catch (error) {
        console.error('마이페이지 데이터를 불러오는 데 실패했습니다.');
      }
    };
    getCompanyNameData();
  }, []);

  return (
    <Container>
      <Title>
        <span>{companyName}</span>
      </Title>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="select-wrapper">
          <span>수배차량</span>
          <Controller
            name="wanted"
            control={control}
            render={({ field }) => (
              <Select
                className="search-select"
                classNamePrefix="react-select"
                options={carType}
                onChange={(val) => field.onChange(val ? val.value : '')}
                value={carType.find((c) => c.value === field.value)}
                components={{ IndicatorSeparator: () => null }}
                placeholder="차량 선택"
                maxMenuHeight={200}
              />
            )}
          />
        </div>
        <div className="row-50">
          <DatePickerContainer>
            <span>도착일</span>
            <div className="inner">
              <Controller
                name="targetDate"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CustomDatePicker
                    className="search-input"
                    locale={ko}
                    dateFormat="yyyy-MM-dd"
                    selected={value}
                    startDate={targetDate}
                    selectsStart
                    onChange={(date: Date | null) => onChange(date)}
                    placeholderText="날짜 선택"
                    withPortal
                    minDate={new Date()} // 오늘 날짜 이전은 선택할 수 없게 설정
                  />
                )}
              />
              <div className="icon-box">
                <Calendar />
              </div>
            </div>
          </DatePickerContainer>
          <DatePickerContainer>
            <span>도착시간</span>
            <div className="inner">
              <Controller
                name="targetTime"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CustomDatePicker
                    className="search-input"
                    selected={value}
                    onChange={(date: Date | null) => onChange(date)}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="도착시간"
                    dateFormat="hh:mm aa"
                    withPortal
                    placeholderText={startTime.toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })} // 문자열로 변환
                  />
                )}
              />
              <div className="icon-box">
                <TimeLine />
              </div>
            </div>
          </DatePickerContainer>
        </div>
        <div className="row-50">
          <div className="select-wrapper">
            <span>작업형태</span>
            <Controller
              name="work"
              control={control}
              render={({ field }) => (
                <Select
                  className="search-select"
                  classNamePrefix="react-select"
                  options={workTypeSelectItems}
                  onChange={(val) => field.onChange(val ? val.value : '')}
                  value={workTypeSelectItems.find(
                    (c) => c.value === field.value,
                  )}
                  components={{ IndicatorSeparator: () => null }}
                  placeholder="작업 형태 선택"
                  maxMenuHeight={200}
                />
              )}
            />
          </div>
          <div className="wrapper">
            <Controller
              name="totalVehicle"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Input
                  label="필요대수"
                  name="totalVehicle"
                  value={value ? formatNumber(Number(value)) : ''}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/[^0-9]/g, '');
                    onChange(rawValue ? Number(rawValue) : 0); // 숫자형으로 변환하여 상태 업데이트
                  }}
                  placeholder="숫자 입력"
                />
              )}
            />
          </div>
        </div>
        <div className="wrapper">
          <Controller
            name="amount"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                label="금액"
                name="amount"
                value={value ? formatNumber(Number(value)) : ''}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/[^0-9]/g, '');
                  onChange(rawValue ? Number(rawValue) : 0); // 숫자형으로 변환하여 상태 업데이트
                }}
                placeholder="숫자 입력"
              />
            )}
          />
        </div>
        <AddOrderBox>
          <div className="top">
            <span>추가 시간</span>
            <button type="button" onClick={addOrder}>
              추가
            </button>
          </div>
          <ul className="list-box">
            {orders.map((order, index) => (
              <li key={index}>
                <div className="row-50">
                  <DatePickerContainer>
                    <span>도착일</span>
                    <div className="inner">
                      <Controller
                        name={`orders.${index}.targetDate`}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <CustomDatePicker
                            className="search-input"
                            locale={ko}
                            dateFormat="yyyy-MM-dd"
                            selected={value}
                            onChange={(date: Date | null) => onChange(date)}
                            placeholderText="날짜 선택"
                            withPortal
                            minDate={new Date()} // 오늘 날짜 이전은 선택할 수 없게 설정
                          />
                        )}
                      />
                      <div className="icon-box">
                        <Calendar />
                      </div>
                    </div>
                  </DatePickerContainer>
                  <DatePickerContainer>
                    <span>도착시간</span>
                    <div className="inner">
                      <Controller
                        name={`orders.${index}.targetTime`}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <CustomDatePicker
                            className="search-input"
                            selected={value}
                            onChange={(date: Date | null) => onChange(date)}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={15}
                            timeCaption="도착시간"
                            dateFormat="hh:mm aa"
                            withPortal
                            placeholderText={startTime.toLocaleTimeString(
                              'en-US',
                              {
                                hour: '2-digit',
                                minute: '2-digit',
                              },
                            )}
                          />
                        )}
                      />
                      <div className="icon-box">
                        <TimeLine />
                      </div>
                    </div>
                  </DatePickerContainer>
                </div>
                <div className="row-50">
                  <div className="select-wrapper">
                    <span>작업형태</span>
                    <Controller
                      name={`orders.${index}.work`}
                      control={control}
                      render={({ field }) => (
                        <Select
                          className="search-select"
                          classNamePrefix="react-select"
                          options={workTypeSelectItems}
                          onChange={(val) =>
                            field.onChange(val ? val.value : '')
                          }
                          value={workTypeSelectItems.find(
                            (c) => c.value === field.value,
                          )}
                          components={{ IndicatorSeparator: () => null }}
                          placeholder="작업 형태 선택"
                          maxMenuHeight={200}
                        />
                      )}
                    />
                  </div>
                  <div className="wrapper">
                    <Controller
                      name={`orders.${index}.totalVehicle`}
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <Input
                          label="필요대수"
                          name={`orders[${index}].totalVehicle`}
                          value={value ? formatNumber(Number(value)) : ''}
                          onChange={(e) => {
                            const rawValue = e.target.value.replace(
                              /[^0-9]/g,
                              '',
                            );
                            onChange(rawValue ? Number(rawValue) : 0); // 숫자형으로 변환하여 상태 업데이트
                          }}
                          placeholder="숫자 입력"
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="wrapper">
                  <Controller
                    name={`orders.${index}.amount`}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Input
                        label="금액"
                        name={`orders[${index}].amount`}
                        value={value ? formatNumber(Number(value)) : ''}
                        onChange={(e) => {
                          const rawValue = e.target.value.replace(
                            /[^0-9]/g,
                            '',
                          );
                          onChange(rawValue ? Number(rawValue) : 0); // 숫자형으로 변환하여 상태 업데이트
                        }}
                        placeholder="숫자 입력"
                      />
                    )}
                  />
                </div>
                <div className="btn-box">
                  <button type="button" onClick={() => removeOrder(index)}>
                    삭제
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </AddOrderBox>
        <ConfirmBtn onClick={() => {}}>등록 하기</ConfirmBtn>
      </form>

      {isModalOpen && (
        <AlertModal
          handleConfirmClose={handleModalClose}
          confirmText={modalMessage}
          onClick={handleModalClose}
        />
      )}
    </Container>
  );
};

export default OrderRegist;

// style
const Container = styled.div`
  padding: 0 2rem;
  height: calc(100% - 11rem);
  overflow: scroll;
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    .select-wrapper {
      padding: 1.1rem 1.1rem 1.6rem 1.5rem;
      background-color: ${(props) => props.theme.gray100};
      border-radius: 0.8rem;
      display: flex;
      flex-direction: column;
      font-size: 1.6rem;
      font-weight: 600;
      span {
        font-size: 1.2rem;
        font-weight: 600;
        color: ${(props) => props.theme.joinLabelColor};
        margin-bottom: 0.6rem;
        line-height: 1.5rem;
      }
      .search-select {
        .react-select__placeholder {
          color: ${(props) => props.theme.placeholderColor};
          font-weight: 400;
        }
        .react-select__control {
          background-color: transparent;
          min-height: 2rem;
          height: 2rem;
          border: none;
          .react-select__value-container {
            padding: 0;
          }
        }
        .react-select__indicator {
          padding: 0;
        }
      }
    }
    .set-cost-title {
      margin: 0.4rem 1rem;
      font-size: 1.4rem;
      font-weight: 600;
    }
    .cost-wrapper {
      div {
        > div {
          background-color: ${(props) => props.theme.costBgColor};
        }
      }
    }
    .row-50 {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 0.9rem;
      .cost-wrapper {
        div {
          > div {
            background-color: ${(props) => props.theme.costBgColor};
          }
        }
      }
    }
    .etc-setting-btn-box {
      display: flex;
      button {
        padding: 1.8rem 1rem;
        outline: none;
        border: none;
        background-color: transparent;
        display: flex;
        align-items: center;
        gap: 1.2rem;
        span {
          font-size: 1.6rem;
          font-weight: 600;
          line-height: 2rem;
        }
        .icon-box {
          display: flex;
        }
      }
    }
  }
`;

const Title = styled.div`
  padding: 1.2rem 0 1.6rem;
  span {
    font-size: 1.6rem;
    font-weight: 700;
  }
`;

const CustomDatePicker = styled(DatePicker)``;

const DatePickerContainer = styled.div`
  padding: 1.1rem 1.1rem 1.6rem 1.5rem;
  background-color: ${(props) => props.theme.gray100};
  border-radius: 0.8rem;
  display: flex;
  flex-direction: column;
  span {
    font-size: 1.2rem;
    font-weight: 600;
    color: ${(props) => props.theme.joinLabelColor};
    margin-bottom: 0.6rem;
    line-height: 1.5rem;
  }
  .inner {
    position: relative;
    width: 100%;
    min-height: 2.4rem;
    display: flex;
    align-items: flex-end;
    .search-input {
      width: 100%;
      font-size: 1.6rem;
      font-weight: 400;
      background-color: inherit;
      border: 0px;
      outline: none;
      &::placeholder {
        color: ${(props) => props.theme.placeholderColor};
      }
    }
    .react-datepicker {
      font-size: 1.4rem;
      font-weight: 400;
      .react-datepicker__header {
        background-color: ${(props) => props.theme.textWhiteColor};
      }
      .react-datepicker__time-container
        .react-datepicker__time
        .react-datepicker__time-box {
        width: 20rem;
      }
      .react-datepicker__time-container {
        width: 20rem;
      }
      .react-datepicker__time-container
        .react-datepicker__time
        .react-datepicker__time-box
        ul.react-datepicker__time-list
        li.react-datepicker__time-list-item {
        width: 100%;
        height: 4.2rem;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 1.8rem;
      }
      .react-datepicker__current-month,
      .react-datepicker-time__header,
      .react-datepicker-year-header {
        font-size: 1.4rem;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .react-datepicker__day-name,
      .react-datepicker__day,
      .react-datepicker__time-name {
        width: 3rem;
        line-height: 3rem;
      }
      .react-datepicker__day--selected,
      .react-datepicker__day--in-selecting-range,
      .react-datepicker__day--in-range,
      .react-datepicker__month-text--selected,
      .react-datepicker__month-text--in-selecting-range,
      .react-datepicker__month-text--in-range,
      .react-datepicker__quarter-text--selected,
      .react-datepicker__quarter-text--in-selecting-range,
      .react-datepicker__quarter-text--in-range,
      .react-datepicker__year-text--selected,
      .react-datepicker__year-text--in-selecting-range,
      .react-datepicker__year-text--in-range {
        border-radius: 2rem;
        background-color: ${(props) => props.theme.btnBlueColor};
        color: ${(props) => props.theme.textWhiteColor};
      }
    }
    .react-datepicker-popper[data-placement='bottom']
      .react-datepicker__triangle {
      fill: ${(props) => props.theme.textWhiteColor};
      color: ${(props) => props.theme.textWhiteColor};
    }
    .icon-box {
      display: flex;
      position: absolute;
      top: 50%;
      right: 0.1rem;
      transform: translateY(-50%);
      svg {
        path {
          fill: #d7dbde;
        }
      }
    }
  }
`;

const AddOrderBox = styled.section`
  margin-top: 0.3rem;
  .top {
    padding: 1.3rem 0 1.3rem 1rem;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    span {
      font-size: 1.6rem;
      font-weight: 600;
    }
    button {
      border: none;
      outline: none;
      background-color: ${(props) => props.theme.btnBlueColor};
      color: ${(props) => props.theme.textWhiteColor};
      width: 4.9rem;
      height: 3rem;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 0.4rem;
      font-size: 1.3rem;
      font-weight: 400;
      line-height: 2.5rem;
    }
  }
  ul {
    display: flex;
    flex-direction: column;
    gap: 1.6rem;
    li {
      display: flex;
      flex-direction: column;
      gap: 1.2rem;
    }
  }
  .btn-box {
    display: flex;
    button {
      width: 100%;
      border: none;
      outline: none;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 0.4rem;
      font-size: 1.3rem;
      font-weight: 400;
      padding: 0.8rem;
      background-color: ${(props) => props.theme.errorColor};
      color: ${(props) => props.theme.textWhiteColor};
    }
  }
`;

const ConfirmBtn = styled.button`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  min-height: 9.4rem;
  border: none;
  outline: none;
  padding: 1.7rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background-color: ${(props) => props.theme.btnStrongBlueColor};
  color: ${(props) => props.theme.textWhiteColor};
  font-size: 1.8rem;
  font-weight: 600;
`;
